import * as RadixSelect from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * A native <select> cannot be styled to the instrument, and a div with a click handler is
 * not a select. Radix gives us a real listbox: keyboard, typeahead, aria — and it reads the
 * document direction, so it opens on the correct side in Arabic without being told.
 */

/**
 * Radix forbids an empty-string item value — it reserves "" to mean "nothing is selected",
 * and throws if an option uses it. "All sites" and "No generator" are real, selectable
 * choices, so they get a real sentinel value and the caller maps it back to `undefined`.
 */
export const NONE = "__none__";

export interface SelectOption {
  value: string;
  label: string;
  hint?: string;
  disabled?: boolean;
}

export interface SelectProps {
  value: string | undefined;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  className?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string | undefined;
  "aria-label"?: string;
}

export function Select({
  value,
  onValueChange,
  options,
  placeholder,
  id,
  disabled,
  className,
  ...aria
}: SelectProps) {
  return (
    <RadixSelect.Root value={value} onValueChange={onValueChange} disabled={disabled}>
      <RadixSelect.Trigger
        id={id}
        className={cn(
          "flex h-10 w-full items-center justify-between gap-2 rounded-[6px]",
          "border border-rule-strong bg-paper px-3 text-sm text-text",
          "focus-visible:border-ink focus-visible:outline-2 focus-visible:outline-offset-[-1px] focus-visible:outline-ink",
          "disabled:cursor-not-allowed disabled:bg-paper-sunk disabled:text-muted",
          "data-[placeholder]:text-faint",
          "aria-[invalid=true]:border-dry",
          className,
        )}
        {...aria}
      >
        <RadixSelect.Value placeholder={placeholder} />
        <RadixSelect.Icon>
          <ChevronDown className="size-4 text-faint" aria-hidden />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>

      <RadixSelect.Portal>
        <RadixSelect.Content
          position="popper"
          sideOffset={4}
          className={cn(
            "z-50 max-h-64 min-w-[var(--radix-select-trigger-width)] overflow-hidden",
            "rounded-[6px] border border-rule-strong bg-paper",
            "shadow-[0_8px_28px_rgba(57,54,70,0.14)]",
          )}
        >
          <RadixSelect.Viewport className="p-1">
            {options.map((option) => (
              <RadixSelect.Item
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className={cn(
                  "relative flex cursor-default select-none items-center gap-2 rounded-[4px]",
                  "py-2 pe-8 ps-2 text-sm text-text outline-none",
                  "data-[highlighted]:bg-paper-sunk",
                  "data-[disabled]:pointer-events-none data-[disabled]:opacity-45",
                )}
              >
                <div className="flex min-w-0 flex-col">
                  <RadixSelect.ItemText>{option.label}</RadixSelect.ItemText>
                  {option.hint ? (
                    <span className="truncate text-xs text-faint">{option.hint}</span>
                  ) : null}
                </div>
                <RadixSelect.ItemIndicator className="absolute end-2">
                  <Check className="size-4 text-ink" aria-hidden />
                </RadixSelect.ItemIndicator>
              </RadixSelect.Item>
            ))}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}
